<template>
  <div class="automation-compare">
    <field-block>
      <v-row wrap class="compare-row compare-row--first">
        <v-col
          cols="12"
          sm="6"
          v-for="(automationItem, idx) in mergedAutomations"
          :key="idx"
        >
          <automation-card
            :automation="automationItem"
            :showActions="false"
            :highlightText="idx === 1"
            :dateFromFields="idx === 1"
            :group-contact-counts="groupContactCounts(automationItem)"
          />
        </v-col>
      </v-row>
    </field-block>

    <template v-if="automation.type === 'event'">
      <field-block title="Event Name">
        <v-row wrap class="compare-row">
          <v-col
            v-for="(automationItem, idx) in mergedAutomations"
            :key="idx"
            cols="12"
            sm="6"
          >
            <div class="compare-row__content">
              {{ automationItem.event }}
            </div>
          </v-col>
        </v-row>
      </field-block>

      <field-block v-if="automation.properties.length" title="Event properties">
        <v-row wrap class="compare-row">
          <v-col
            v-for="(automationItem, idx) in mergedAutomations"
            :key="idx"
            cols="12"
            sm="6"
          >
            <div class="compare-row__content">
              <ul>
                <li
                  v-for="(property, idx) in automationItem.properties"
                  :key="idx"
                >
                  <strong class="pr-2">{{ property.answer }}:</strong>
                  {{ property.points }}
                </li>
              </ul>
            </div>
          </v-col>
        </v-row>
      </field-block>
    </template>

    <template v-if="automation.type === 'tag'">
      <field-block
        v-if="automation.add_tags.length || automationOriginal.add_tags.length"
        title="Added tags"
      >
        <v-row wrap class="compare-row">
          <v-col
            cols="12"
            sm="6"
            v-for="(automationItem, idx) in mergedAutomations"
            :key="idx"
          >
            <div class="compare-row__content">
              <v-chip
                color="primary"
                text-color="white"
                v-for="(tag, idx) in automationItem.add_tags"
                :key="idx"
                >{{ tag }}</v-chip
              >
            </div>
          </v-col>
        </v-row>
      </field-block>

      <field-block
        v-if="
          automation.remove_tags.length || automationOriginal.remove_tags.length
        "
        title="Removed tags"
      >
        <v-row wrap class="compare-row">
          <v-col
            cols="12"
            sm="6"
            v-for="(automationItem, idx) in mergedAutomations"
            :key="idx"
          >
            <div class="compare-row__content">
              <v-chip
                color="error"
                text-color="white"
                v-for="(tag, idx) in automationItem.remove_tags"
                :key="idx"
                >{{ tag }}</v-chip
              >
            </div>
          </v-col>
        </v-row>
      </field-block>
    </template>

    <template v-if="automation.type === 'join'">
      <field-block
        v-if="
          automation.confirm_email_subject ||
          automationOriginal.confirm_email_subject
        "
        title="Custom email"
      >
        <v-row wrap class="compare-row">
          <v-col
            cols="12"
            sm="6"
            v-for="(automationItem, idx) in mergedAutomations"
            :key="idx"
          >
            <div class="compare-row__content">
              <p>
                <strong>Subject:</strong>
                {{ automationItem.confirm_email_subject }}
              </p>
              <p>
                <strong>Message:</strong>
                {{ automationItem.confirm_email_response }}
              </p>
            </div>
          </v-col>
        </v-row>
      </field-block>

      <field-block
        v-if="automation.welcome_sms || automationOriginal.welcome_sms"
        title="Custom Message"
      >
        <v-row wrap class="compare-row">
          <v-col
            cols="12"
            sm="6"
            v-for="(automationItem, idx) in mergedAutomations"
            :key="idx"
          >
            <div class="compare-row__content">
              {{ automationItem.welcome_sms }}
            </div>
          </v-col>
        </v-row>
      </field-block>
    </template>

    <field-block title="Webhook URL" v-if="automation.type === 'hook'">
      <v-row wrap class="compare-row">
        <v-col
          cols="12"
          sm="6"
          v-for="(automationItem, idx) in mergedAutomations"
          :key="idx"
        >
          <div class="compare-row__content">
            <a :href="automationItem.url">{{ automationItem.url }}</a>
          </div>
        </v-col>
      </v-row>
    </field-block>

    <field-block title="Contacts affected">
      <p>
        Please note that since this Automation has been scheduled for a later
        date and time, the list of affected Contacts may be different at the
        moment of release.
      </p>

      <p v-if="usesAdditionalQuery" class="mb-3">
        {{ contactsSummary(automation) }}
      </p>

      <v-layout
        v-if="isContactsLoading"
        justify-center
        align-center
        class="pa-5"
      >
        <spinner />
      </v-layout>

      <template v-else>
        <v-row wrap class="compare-row">
          <v-col
            cols="12"
            sm="6"
            v-for="(automationItem, idx) in mergedAutomations"
            :key="idx"
          >
            <div
              v-if="groupUsers(automationItem).length"
              class="v-table__overflow"
            >
              <table class="v-table">
                <thead>
                  <tr>
                    <th>{{ idx === 1 ? 'Updated' : 'Previous' }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(user, userIdx) in groupUsers(automationItem)"
                    :key="userIdx"
                  >
                    <td>{{ getContactName(user) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="compare-row__content" v-else>
              {{ emptyContactsMessage(automationItem) }}
            </div>
          </v-col>
        </v-row>
      </template>
    </field-block>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AutomationCard from '@/views/automations/components/AutomationCard';
import {
  getAutomationAdditionalQuery,
  getAutomationGroupFilters,
  loadAutomationContactsPreview,
} from './automationConfirmHelpers';

export default {
  components: {
    AutomationCard,
  },
  props: {
    automation: {
      type: Object,
      default: () => ({}),
    },
    automationOriginal: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      localContactsLoading: false,
      contactsPreviews: {},
    };
  },
  computed: {
    ...mapGetters([
      'groupContactsLoading',
      'singleGroupContactsInfo',
      'singleGroup',
    ]),
    mergedAutomations() {
      return [this.automationOriginal, this.automation];
    },
    usesAdditionalQuery() {
      return this.mergedAutomations.some((item) =>
        getAutomationAdditionalQuery(item)
      );
    },
    isContactsLoading() {
      return this.usesAdditionalQuery
        ? this.localContactsLoading
        : this.groupContactsLoading;
    },
  },
  methods: {
    ...mapActions(['loadGroupContacts']),
    getGroup(groupId) {
      return this.singleGroup(groupId);
    },
    previewKey(automationItem) {
      return automationItem.bookmark_id;
    },
    getPreview(automationItem) {
      return this.contactsPreviews[this.previewKey(automationItem)] || {};
    },
    groupContactCounts(automationItem) {
      const additionalQuery = getAutomationAdditionalQuery(automationItem);
      if (!additionalQuery) {
        return null;
      }

      const preview = this.getPreview(automationItem);

      return {
        eligible: preview.eligibleCount,
        total: preview.totalCount,
      };
    },
    contactsSummary(automationItem) {
      const preview = this.getPreview(automationItem);
      const eligible = (preview.eligibleCount || 0).toLocaleString('en-US');
      const total = (preview.totalCount || 0).toLocaleString('en-US');

      return `${eligible} contacts with a mobile number (of ${total} in this group)`;
    },
    emptyContactsMessage(automationItem) {
      if (getAutomationAdditionalQuery(automationItem)) {
        return 'There are no contacts with a mobile number in this group.';
      }

      return 'There are no users in this group.';
    },
    groupUsers(automationItem) {
      if (getAutomationAdditionalQuery(automationItem)) {
        return this.getPreview(automationItem).users || [];
      }

      const group =
        this.singleGroupContactsInfo(automationItem.bookmark_id) || {};
      return group.users || [];
    },
    async loadContactsPreview(automationItem) {
      const group = this.getGroup(automationItem.bookmark_id);
      const groupFilters = getAutomationGroupFilters(automationItem, group);
      const additionalQuery = getAutomationAdditionalQuery(automationItem);

      if (!groupFilters || !additionalQuery) {
        return;
      }

      const key = this.previewKey(automationItem);
      const preview = await loadAutomationContactsPreview({
        groupFilters,
        additionalQuery,
      });

      this.$set(this.contactsPreviews, key, preview);
    },
    async loadAllContactsPreviews() {
      const seenBookmarkIds = new Set();
      const automations = this.mergedAutomations.filter((item) => {
        if (
          !getAutomationAdditionalQuery(item) ||
          !item.bookmark_id ||
          seenBookmarkIds.has(item.bookmark_id)
        ) {
          return false;
        }

        seenBookmarkIds.add(item.bookmark_id);
        return true;
      });

      if (!automations.length) {
        return;
      }

      this.localContactsLoading = true;

      try {
        await Promise.all(
          automations.map((item) => this.loadContactsPreview(item))
        );
      } finally {
        this.localContactsLoading = false;
      }
    },
    loadDefaultGroupContacts() {
      const groupsIds = [
        ...new Set(this.mergedAutomations.map((i) => i.bookmark_id)),
      ];

      groupsIds.forEach((groupId) => {
        if (!this.groupUsers({ bookmark_id: groupId }).length) {
          this.loadGroupContacts({
            group: this.getGroup(groupId),
          });
        }
      });
    },
  },
  mounted() {
    if (this.usesAdditionalQuery) {
      this.loadAllContactsPreviews();
      return;
    }

    this.loadDefaultGroupContacts();
  },
};
</script>

<style lang="less">
.compare-row {
  position: relative;

  &__content {
    border-radius: @base-radius;
    background-color: #fff;
    overflow: hidden;
    padding: 12px;
    font-weight: 600;
    font-size: 14px;
  }
}
.v-table {
  .dark-bg th {
    background-color: var(--v-appMain-base);
    color: var(--v-light-base) !important;
  }
}
</style>
